import { httpsCallable } from 'firebase/functions'
import { loadStripe, type Stripe } from '@stripe/stripe-js'
import { functions } from '../firebase/config'
import type { PaymentMethod, Invoice } from '../types/billing'

export interface SubscriptionStatus {
  tier: string
  tierName: string
  monthlyTokenLimit: number
  priceEur: number
  subscriptionId: string | null
  subscriptionStatus: string | null
  currentPeriodEnd: number | null
}

class StripeService {
  private static stripePromise: Promise<Stripe | null> | null = null

  static getStripe(): Promise<Stripe | null> {
    if (!this.stripePromise) {
      this.stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY)
    }
    return this.stripePromise
  }

  static async createSetupIntent(): Promise<{ clientSecret: string }> {
    const fn = httpsCallable(functions, 'createSetupIntent')
    const result = await fn({})
    return result.data as { clientSecret: string }
  }

  static async createSubscription(priceId: string, paymentMethodId: string): Promise<{
    subscriptionId: string
    clientSecret: string | null
    status: string
  }> {
    const fn = httpsCallable(functions, 'createSubscription')
    const result = await fn({ priceId, paymentMethodId })
    return result.data as {
      subscriptionId: string
      clientSecret: string | null
      status: string
    }
  }

  static async getCustomerPortalUrl(returnUrl?: string): Promise<{ url: string }> {
    const fn = httpsCallable(functions, 'getCustomerPortalUrl')
    const result = await fn({ returnUrl: returnUrl || window.location.href })
    return result.data as { url: string }
  }

  static async getSubscriptionStatus(): Promise<SubscriptionStatus> {
    const fn = httpsCallable(functions, 'getSubscriptionStatus')
    const result = await fn({})
    return result.data as SubscriptionStatus
  }

  static async getPaymentMethods(): Promise<PaymentMethod[]> {
    const fn = httpsCallable(functions, 'getPaymentMethods')
    const result = await fn({})
    return (result.data as { paymentMethods: PaymentMethod[] }).paymentMethods
  }

  static async getInvoices(limit = 10): Promise<Invoice[]> {
    const fn = httpsCallable(functions, 'getInvoices')
    const result = await fn({ limit })
    return (result.data as { invoices: Invoice[] }).invoices
  }

  static async cancelSubscription(immediate = false): Promise<{
    success: boolean
    canceledAt: number | null
    cancelAtPeriodEnd: boolean
  }> {
    const fn = httpsCallable(functions, 'cancelSubscription')
    const result = await fn({ immediate })
    return result.data as {
      success: boolean
      canceledAt: number | null
      cancelAtPeriodEnd: boolean
    }
  }
}

export default StripeService
